/* Plantica — the offer box (Rapi Bundle), laid out like the client's design of
   20.10.2026, revised 23.10.2026: a pictogram per product, what the offer saves
   on a band of its own, and the chosen offer's price on the „Adaugă în coș"
   buttons. Data and copy come from snippets/plantica-rapi.liquid.

   NOTHING IS ADDED TO RAPI'S MARKUP

   Rapi draws the box with its own framework and redraws parts of it as offers
   are chosen. An element it does not know about could be thrown away, or trip
   its next redraw. So inside the box this script only writes data-* attributes
   on elements Rapi already renders, and plantica-rapi.css draws from them:

     data-plantica-icon, -label   on a product's picture → pictogram and name
     data-plantica-qty            on the same picture → „×2", the offer's
                                  quantity in Rapi (see quantityOf)
     data-plantica-savings        on the block the saving's band sits in
     data-plantica-subtitle       on Rapi's heading → the line under it
     data-plantica-gift           on the offer's row, when it gives a product
                                  away → the gift's own square beside the
                                  offer's: its pictogram, or „photo"
     data-plantica-gift-label,    on the block of the offer's words → the gift
     data-plantica-gift-badge     square's name under it and its badge
     data-plantica-empty          on an offer's subtitle with nothing to read
                                  (spaces, an empty paragraph) → no line

   Only the gift's photo, when no pictogram names the gift, goes in as a custom
   property on the row's inline style (`--plantica-rapi-gift-image`).

   Everything is read off what Rapi shows and holds — which offers are combos,
   which give a product away, how many each is — never tied to a product.

   Rapi updates its elements in place when an offer is chosen (checked on
   20.10.2026: the same nodes, a class swapped), so the attributes survive; where
   it does replace one, the observer at the end writes them again.

   THE BUTTONS ARE THE THEME'S

   There a <span> is added after the label. The theme rewrites the label — the
   button's first span — when a variant changes, never the rest of the button.

   The bottom bar does not add what the box offers when the offer is a combo:
   Rapi adds the other products of a combo only from the page's own button, so
   „Setul complet" chosen and the bar pressed put one shampoo in the cart
   (seen on 20.10.2026, POSTs captured). For a combo, the bar's click is passed
   to the page's button, which adds exactly what the price on both says.
   A quantity offer („2 Seturi") the bar already adds whole.

   Custom file. See CUSTOMIZATIONS.md. */

(() => {
  const source = document.getElementById('PlanticaRapiData');
  if (!source || window.planticaRapiReady) return;

  let data;
  try {
    data = JSON.parse(source.textContent);
  } catch (error) {
    return;
  }
  window.planticaRapiReady = true;

  const MAIN_BUTTON = '[id^="ProductSubmitButton-"][id$="__main"]';
  // Rapi draws a combo's products in one of two layouts, „royal" (a list) or
  // „block" (side by side) — the Ulei A/B test has one of each.
  const COMBO_ITEM = '.rapi--royal-combo-bundle, .rapi--block-combo-bundle';
  const BAR_FORM = '#product-form-sticky-atc-bar';

  /* ---------- reading Rapi's text ---------- */

  const plain = (text) =>
    (text || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();

  const icons = (data.icons || []).map((icon) => ({
    ...icon,
    pattern: new RegExp(`\\b(${icon.words.join('|')})\\b`),
  }));

  const identify = (title) => {
    const text = plain(title);
    return icons.find((icon) => icon.pattern.test(text)) || null;
  };

  // „2 Seturi Anti-Cădere", „3 x Șampon" → 2, 3. Anything else is one.
  const quantity = (title) => {
    const match = (title || '').trim().match(/^(\d+)\s*(?:x|×)?\s+\S/i);
    return match ? Number(match[1]) : 1;
  };

  // Rapi's config can hold its texts as HTML („<p>3 Seturi Anti-Cădere</p>").
  const words = (html) => (html || '').replace(/<[^>]*>/g, ' ');
  const same = (a, b) => plain(words(a)).replace(/\s+/g, ' ').trim() === plain(b).replace(/\s+/g, ' ').trim();

  // How many of the product an offer is. Rapi's own number first —
  // `offers[i].quantity` in window.rapi_bundle_<product>, the offer found by the
  // id of its radio („rapi-bundles-deal-<i>-<product>") — when that offer has
  // the title shown on the page: under an A/B test the page can show another
  // list than the one there. Else the number the title starts with.
  const quantityOf = (card) => {
    const title = card.querySelector('.rapi-bundles__bar-title');
    const text = title ? title.textContent : '';
    const input = card.previousElementSibling;
    const match = input && input.id ? input.id.match(/^rapi-bundles-deal-(\d+)-(\d+)$/) : null;
    const config = match ? window[`rapi_bundle_${match[2]}`] : null;
    const offer = config && Array.isArray(config.offers) ? config.offers[Number(match[1])] : null;
    if (offer && Number(offer.quantity) > 0 && same(offer.title, text)) return Number(offer.quantity);
    return quantity(text);
  };

  // Money as Rapi prints it — „129,00 lei", „115 lei", „€8,95", „1.234,50 lei" —
  // read into cents, and written back in the same shape.
  const parse = (text) => {
    const match = (text || '').match(/\d[\d\s.,]*/);
    if (!match) return null;
    const raw = match[0].trim();
    const decimals = raw.match(/[.,](\d{1,2})$/);
    const integer = decimals ? raw.slice(0, -decimals[0].length) : raw;
    return {
      cents: Number(integer.replace(/\D/g, '')) * 100 + Number(decimals ? decimals[1].padEnd(2, '0') : 0),
      text: text.replace(/\s+/g, ' ').trim(),
      raw,
      separator: decimals ? decimals[0][0] : ',',
      places: decimals ? decimals[1].length : 0,
      group: (integer.match(/[.\s,]/) || [''])[0],
    };
  };

  const format = (cents, like) => {
    let integer = String(Math.floor(cents / 100));
    if (like.group) integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, like.group);
    const places = like.places || (cents % 100 ? 2 : 0);
    const fraction = places ? like.separator + String(cents % 100).padStart(2, '0').slice(0, places) : '';
    return like.text.replace(like.raw, integer + fraction);
  };

  const set = (element, name, value) => {
    if (!element) return;
    if (value === null || value === undefined || value === '') {
      if (element.hasAttribute(name)) element.removeAttribute(name);
    } else if (element.getAttribute(name) !== String(value)) {
      element.setAttribute(name, value);
    }
  };

  const isCombo = (card) => !!(card && card.querySelector(COMBO_ITEM));
  const chosenCard = () => document.querySelector('.rapi-bundles input:checked + .rapi-bundles__bar-container');

  /* ---------- the box ---------- */

  const dress = (picture, product, count) => {
    set(picture, 'data-plantica-icon', product ? product.key : null);
    set(picture, 'data-plantica-label', product ? product.label : null);
    set(picture, 'data-plantica-qty', count > 1 ? count : null);
  };

  const tier = (card) => {
    const main = card.querySelector('.rapi-bundles__bar-main');
    const items = [...card.querySelectorAll(COMBO_ITEM)];

    const subtitle = card.querySelector('.rapi-bundles__bar-subtitle');
    set(subtitle, 'data-plantica-empty', subtitle && !subtitle.textContent.trim() ? 'true' : null);

    if (items.length) {
      items.forEach((item) => {
        const name = item.querySelector('.rapi--cb-product-title');
        dress(item.querySelector('.offer-image-preview'), identify(name && name.textContent), 1);
      });
    } else if (main) {
      dress(main.querySelector('.offer-image-preview'), identify(data.product), quantityOf(card));
    }

    // A product the offer gives away. Rapi's gift lines are words, with a
    // picture when the merchant gives one, and its config tells a product from
    // free shipping by nothing (no product on either, 23.10.2026 — the set given
    // with three is BOGOS's). So a line is a product when its words name one we
    // have a pictogram for („Încă 1 Set CADOU" → Set) or when it has a picture;
    // „Transport GRATUIT" has neither. Marked on the line for its band, and on
    // an offer that is not a combo the first one gets a square of its own beside
    // the offer's: its pictogram, or, naming nothing known, its photo.
    let giftLine = null;
    let gift = null;
    card.querySelectorAll('.rapi-gift-product-inline').forEach((line) => {
      const text = line.querySelector('.rapi-gift-text');
      const named = identify(text && text.textContent);
      const product = !!(named || line.querySelector('.offer-image-preview img'));
      set(line, 'data-plantica-gift-line', product ? 'product' : null);
      if (product && !giftLine) {
        giftLine = line;
        gift = named;
      }
    });
    const photo = giftLine && !gift ? giftLine.querySelector('.offer-image-preview img') : null;
    const square = !items.length && (gift || (photo && photo.src));
    const block = card.querySelector('.rapi-bundles__bar-content-left');
    set(main, 'data-plantica-gift', square ? (gift ? gift.key : 'photo') : null);
    if (main) {
      if (square && !gift) main.style.setProperty('--plantica-rapi-gift-image', `url("${photo.src}")`);
      else if (main.style.getPropertyValue('--plantica-rapi-gift-image')) main.style.removeProperty('--plantica-rapi-gift-image');
    }
    set(block, 'data-plantica-gift-label', square && gift ? gift.label : null);
    set(block, 'data-plantica-gift-badge', square ? data.giftBadge : null);

    // Where the saving's band goes: beside the gift and shipping lines when the
    // offer has some — the one place it can pair up with them on a desktop —
    // else under a combo's products, which Rapi keeps in a block of its own
    // below the offer's row, else on that row.
    const gifts = [...card.querySelectorAll('.rapi-gift-upsell-wrapper')].find((block) =>
      block.querySelector('.rapi-gift-product-inline')
    );
    const list = items.length ? items[0].parentElement && items[0].parentElement.parentElement : null;
    const row = gifts || list || main;

    // Whether an offer shows what it saves is the merchant's call, made in Rapi
    // (26.10.2026): a subtitle with an amount in it — „Economisești
    // {{saved_total}}", which Rapi works out, a combo's whole too — asks for the
    // band; no such subtitle, no band. The band says the theme's word and Rapi's
    // amount, written like the offer's price („31 lei" beside „199 lei"). The
    // subtitle itself is not shown (plantica-rapi.css). Nothing saved, no band.
    const amount = subtitle && subtitle.querySelector('.rapi-money');
    const saved = amount ? parse(amount.textContent) : null;
    const price = parse((card.querySelector('.rapi-bundles__bar-price') || {}).textContent);
    const saving = saved && saved.cents > 0 ? `${data.savings} ${format(saved.cents, price || saved)}` : null;

    card.querySelectorAll('[data-plantica-savings]').forEach((element) => {
      if (element !== row) element.removeAttribute('data-plantica-savings');
    });
    set(row, 'data-plantica-savings', saving);
  };

  const heading = () => {
    document.querySelectorAll('.rapi-header-title').forEach((title) => {
      set(title, 'data-plantica-subtitle', data.subtitle || null);
    });
  };

  /* ---------- the buttons ---------- */

  const barQuantity = () => {
    const input = document.querySelector(`${BAR_FORM} [name="quantity"]`);
    const value = input ? parseInt(input.value, 10) : 1;
    return value > 0 ? value : 1;
  };

  // What the button will add, priced. On the bar, an offer of one item is sent
  // in the bar's quantity, so it is priced that many times; a combo goes through
  // the page's button, and a quantity offer already sets the bar's quantity.
  const priceFor = (button, card) => {
    const price = parse((card && card.querySelector('.rapi-bundles__bar-price') || {}).textContent);
    if (!price) return '';
    let cents = price.cents;
    if (button.closest(BAR_FORM) && !isCombo(card) && quantityOf(card) === 1) cents *= barQuantity();
    return format(cents, price);
  };

  const buttons = () => {
    const card = data.atcPrice ? chosenCard() : null;
    document.querySelectorAll(`${MAIN_BUTTON}, ${BAR_FORM} [name="add"]`).forEach((button) => {
      let tag = button.querySelector(':scope > .plantica-atc-price');
      const value = card ? priceFor(button, card) : '';
      if (!value) {
        if (tag) tag.remove();
        return;
      }
      if (!tag) {
        tag = document.createElement('span');
        tag.className = 'plantica-atc-price';
        const label = button.querySelector(':scope > span');
        if (label) label.after(tag);
        else button.prepend(tag);
      }
      if (tag.textContent !== value) tag.textContent = value;
    });
  };

  // The bar's click, for a combo, becomes the page button's — before the
  // theme's form or Rapi hear of it.
  window.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest && event.target.closest(`${BAR_FORM} [name="add"]`);
      if (!button || button.disabled || !isCombo(chosenCard())) return;
      const main = document.querySelector(MAIN_BUTTON);
      if (!main || main.disabled) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      main.click();
    },
    true
  );

  /* ---------- keeping up with Rapi ---------- */

  const apply = () => {
    heading();
    document.querySelectorAll('.rapi-bundles .rapi-bundles__bar-container').forEach(tier);
    buttons();
  };

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      apply();
    });
  };

  // Rapi can arrive late — an offer under an A/B test waits 10–17 s for its
  // answer — so the watch starts before it and stays on.
  const roots = [
    document.querySelector('.shopify-section[id$="__main"]'),
    document.getElementById('shopify-section-sticky-atc-bar'),
  ].filter(Boolean);
  const observer = new MutationObserver(schedule);
  (roots.length ? roots : [document.body]).forEach((root) =>
    observer.observe(root, { childList: true, subtree: true, characterData: true })
  );

  // Choosing an offer changes only classes and inline styles in Rapi's box —
  // nothing the observer watches — and Rapi marks the choice itself, so no
  // `change` reaches the page either. The click on the box is the signal; Rapi
  // has redrawn by the next frame, and the later pass is for a slow redraw.
  const soon = () => {
    schedule();
    setTimeout(schedule, 150);
  };
  ['click', 'change', 'keyup'].forEach((type) =>
    document.addEventListener(type, (event) => {
      if (event.target.closest && event.target.closest('.rapi-bundles')) soon();
    })
  );
  ['change', 'input'].forEach((type) =>
    document.addEventListener(type, (event) => {
      if (event.target.closest && event.target.closest(BAR_FORM)) schedule();
    })
  );

  apply();
})();
